import { useMemo } from 'react';

import { useFeatureGate } from '@trello/feature-gate-client';

import { useCardType } from 'app/src/components/CardType';
import { useMirrorCardQuery } from './MirrorCardQuery.generated';
import { useMirrorCardSourceIdFragment } from './MirrorCardSourceIdFragment.generated';

export const useMirrorCardSourceId = (
  name: string,
  cardId: string,
): { mirrorCardSourceId: string | null; mirrorCardError?: string } => {
  const cardType = useCardType(cardId);

  const { value: isMirrorCardsEnabled } = useFeatureGate('xf_mirror_cards');

  const { data } = useMirrorCardSourceIdFragment({
    from: { id: cardId },
  });

  const {
    data: mirrorData,
    error,
    loading,
  } = useMirrorCardQuery({
    variables: { id: data?.mirrorSourceId ?? '' },
    skip: !data?.mirrorSourceId || cardType !== 'mirror',
    waitOn: [],
    errorPolicy: 'all',
    context: {
      returnNotFoundError: true,
      returnNetworkErrors: true,
      operationName: 'MirrorCardQuery',
    },
  });

  const sourceCardId = useMemo(() => {
    return mirrorData?.card?.id ?? null;
  }, [mirrorData?.card?.id]);

  if (loading) {
    return { mirrorCardSourceId: null };
  }

  if (!isMirrorCardsEnabled || cardType !== 'mirror') {
    return { mirrorCardSourceId: null };
  }

  if (error || !sourceCardId) {
    const mirrorCardError =
      error?.message === 'unauthorized card permission requested'
        ? 'notPermitted'
        : 'notFound';
    return {
      mirrorCardSourceId: cardId,
      mirrorCardError,
    };
  }

  return { mirrorCardSourceId: sourceCardId };
};
